import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { supabase } from '../services/supabase'
import { acceptInvitation, rejectInvitation, getMyPendingInvitations } from '../services/onboarding'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from '../components/Card'
import Button from '../components/Button'
import { Mail, CheckCircle2, XCircle, Clock, Building2, UserPlus } from 'lucide-react'
import { formatDate } from '../utils/helpers'
import { useToast } from '../contexts/ToastContext'
import { useNavigate } from 'react-router-dom'

const Invitations = () => {
  const { t } = useTranslation()
  const { reload } = useOrganization()
  const { success, error: showError } = useToast()
  const navigate = useNavigate()
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(null)

  useEffect(() => {
    fetchInvitations()
  }, [])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const data = await getMyPendingInvitations()
      setInvitations(data || [])
    } catch (error) {
      console.error('Error fetching invitations:', error)
      showError('Failed to load invitations. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async (token) => {
    try {
      setProcessing(token)
      const result = await acceptInvitation(token)

      if (result && result.success) {
        success('Invitation accepted! You have been added to the organization.', {
          title: 'Invitation Accepted',
        })
        
        // Wait a bit before reloading to ensure the RPC call is fully complete
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Reload organization context
        await reload()
        
        // Refresh invitations list
        await fetchInvitations()
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard')
        }, 1000)
      } else {
        const errorMsg = result?.error || result?.message || 'Failed to accept invitation'
        showError(errorMsg, {
          title: 'Acceptance Failed',
        })
      }
    } catch (error) {
      console.error('Error accepting invitation:', error)
      
      // Handle abort errors specifically
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        showError('The request was cancelled. Please try again.', {
          title: 'Request Cancelled',
        })
      } else {
        showError(error.message || 'Failed to accept invitation. Please try again.', {
          title: 'Acceptance Failed',
        })
      }
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async (token) => {
    if (!confirm('Are you sure you want to reject this invitation? You can always accept it later if it hasn\'t expired.')) {
      return
    }

    try {
      setProcessing(token)
      const result = await rejectInvitation(token)

      if (result.success) {
        success('Invitation rejected', {
          title: 'Invitation Rejected',
        })
        fetchInvitations()
      } else {
        showError(result.error || 'Failed to reject invitation', {
          title: 'Rejection Failed',
        })
      }
    } catch (error) {
      console.error('Error rejecting invitation:', error)
      showError(error.message || 'Failed to reject invitation. Please try again.', {
        title: 'Rejection Failed',
      })
    } finally {
      setProcessing(null)
    }
  }

  const getDaysRemaining = (expiresAt) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diffTime = expires - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
  }

  if (loading) {
    return (
      <div className="section-container">
        <Card variant="soft" className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitations...</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
            <Mail size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Invitations</h1>
            <p className="text-gray-600">View and respond to organization invitations</p>
          </div>
        </div>
      </motion.div>

      {/* Invitations List */}
      {invitations.length === 0 ? (
        <Card variant="soft" className="p-12 text-center">
          <Mail size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Pending Invitations
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            You don't have any pending invitations at the moment.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {invitations.map((invitation) => {
            const daysRemaining = getDaysRemaining(invitation.expires_at)
            const isProcessing = processing === invitation.token

            return (
              <motion.div
                key={invitation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card variant="soft" className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center">
                          <Building2 size={20} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {invitation.organization_name || 'Organization'}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Invited as <span className="capitalize font-medium">{invitation.role}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 ml-13">
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>
                            {daysRemaining > 0
                              ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
                              : 'Expired'}
                          </span>
                        </div>
                        <span>•</span>
                        <span>Invited {formatDate(invitation.created_at)}</span>
                      </div>
                    </div>
                    {daysRemaining > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => handleReject(invitation.token)}
                          disabled={isProcessing}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <XCircle size={18} className="mr-2" />
                          {isProcessing ? 'Processing...' : 'Reject'}
                        </Button>
                        <Button
                          onClick={() => handleAccept(invitation.token)}
                          disabled={isProcessing}
                          className="flex items-center gap-2"
                        >
                          <CheckCircle2 size={18} />
                          {isProcessing ? 'Processing...' : 'Accept'}
                        </Button>
                      </div>
                    )}
                  </div>
                  {daysRemaining === 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        This invitation has expired. Please contact the organization administrator for a new invitation.
                      </p>
                    </div>
                  )}
                </Card>
              </motion.div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Invitations
